export type EncryptedPayload = {
  iv: string;
  data: string;
};

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const PBKDF2_SALT = encoder.encode("sharebin-v1");
const PBKDF2_ITERATIONS = 600_000;

export function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

export function base64UrlToBytes(value: string): Uint8Array {
  const padded = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

export async function createPasteKey(): Promise<string> {
  const key = (await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"])) as CryptoKey;
  const raw = (await crypto.subtle.exportKey("raw", key)) as ArrayBuffer;
  return bytesToBase64Url(new Uint8Array(raw));
}

export async function importPasteKey(encodedKey: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", toArrayBuffer(base64UrlToBytes(encodedKey)), "AES-GCM", false, ["encrypt", "decrypt"]);
}

export async function deriveKeyFromCode(code: string): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey("raw", encoder.encode(code), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: PBKDF2_SALT, iterations: PBKDF2_ITERATIONS, hash: "SHA-256" },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptBytes(key: CryptoKey, bytes: Uint8Array): Promise<{ iv: string; bytes: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv: toArrayBuffer(iv) }, key, toArrayBuffer(bytes));
  return { iv: bytesToBase64Url(iv), bytes: new Uint8Array(encrypted) };
}

export async function decryptBytes(key: CryptoKey, iv: string, bytes: Uint8Array): Promise<Uint8Array> {
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv: toArrayBuffer(base64UrlToBytes(iv)) }, key, toArrayBuffer(bytes));
  return new Uint8Array(decrypted);
}

export async function encryptText(key: CryptoKey, text: string): Promise<EncryptedPayload> {
  const encrypted = await encryptBytes(key, encoder.encode(text));
  return { iv: encrypted.iv, data: bytesToBase64Url(encrypted.bytes) };
}

export async function decryptText(key: CryptoKey, payload: EncryptedPayload): Promise<string> {
  const decrypted = await decryptBytes(key, payload.iv, base64UrlToBytes(payload.data));
  return decoder.decode(decrypted);
}

export function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  if (bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
    return bytes.buffer as ArrayBuffer;
  }
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer as ArrayBuffer;
}
